import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../api/AxiosClient";

export interface User {
  id: string;
  name: string;
  email: string;
}

export interface Agent {
  id: string;
  name: string;
  category?: string;
}

export interface UserWithAgents {
  user: User;
  agents: Agent[];
}

export interface Pagination {
  limit: number;
  offset: number;
  hasMore: boolean;
  total: number;
}

export interface FetchUsersParams {
  limit?: number;
  offset?: number;
  search?: string;
}

export interface FetchUsersResponse {
  data: User[];
  pagination: Pagination;
}

export interface UserAgentAccessParams {
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search_by_user_name?: string;
  search_by_email?: string;
  search_by_agent_id?: number[];
  search_by_agent_category?: string[];
}

export interface UserAgentAccessResponse {
  data: UserWithAgents[];
  pagination: Pagination;
}

export interface UpdateUserAgentsPayload {
  userId: string;
  add?: string[];
  remove?: string[];
}

export interface UpdateUserAgentsResponse {
  userId: string;
  added: number;
  removed: number;
  currentTotal: number;
  updatedAt: string;
}

export interface DeleteUsersPayload {
  userIds: string[];
}

export interface DeleteUsersResponse {
  deleted: number;
  failed: string[];
}

export interface AddUsersPayload {
  users: { email: string }[];
}

export interface AddUserResult {
  email: string;
  success: boolean;
  message: string;
  user?: {
    id: number;
    name: string;
    email: string;
    status: string;
    createdAt: string;
  };
}

export interface AddUsersResponse {
  totalRequested: number;
  added: number;
  alreadyExists: number;
  results: AddUserResult[];
}

export interface BulkUploadPayload {
  agentId: string;
  addEmails?: string[];
  removeEmails?: string[];
  file?: File;
}

export interface BulkUploadResult {
  row?: number;
  name: string;
  email: string;
  userCreated: boolean;
  agentAssigned: boolean;
  message: string;
  userId: number;
}

export interface BulkUploadResponse {
  totalRows: number;
  usersCreated: number;
  usersExisted: number;
  agentsAssigned: number;
  failed: number;
  agentId: number;
  results: BulkUploadResult[];
}

export interface AssignMultipleAgentsPayload {
  userIds?: number[];
  agentIds?: number[];
  departments?: string[];
  addEmails?: string[];
  file?: File;
}

export interface AssignMultipleAgentsResponse {
  totalRows: number;
  usersCreated: number;
  usersExisted: number;
  agentsAssigned: number;
  failed: number;
  results: BulkUploadResult[];
}

// Fetch users for dropdown (100 at a time, searchable)
export const fetchUsers = createAsyncThunk(
  "userRoles/fetchUsers",
  async (params: FetchUsersParams = {}, { rejectWithValue }) => {
    try {
      const { limit = 100, offset = 0, search = "" } = params;

      const response = await axiosClient.get<FetchUsersResponse>("/users", {
        params: { limit, offset, search }
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch users"
      );
    }
  }
);

// Fetch agents (all at once for now)
export const fetchAgents = createAsyncThunk(
  "userRoles/fetchAgents",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<any[]>("/agents/");
      // Transform the response to match our Agent interface
      // Filter: exclude agent ID 501
      const agents: Agent[] = response.data
        .filter((agent: any) => agent.id !== 501)
        .map((agent: any) => ({
          id: agent.id.toString(),
          name: agent.agent_name,
          category: agent.category || "Other",
        }));
      return agents;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch agents"
      );
    }
  }
);

// Fetch users with their assigned agents (50 at a time, with filters)
export const fetchUserAgentMappings = createAsyncThunk(
  "userRoles/fetchUserAgentMappings",
  async (params: UserAgentAccessParams = {}, { rejectWithValue }) => {
    try {
      const {
        limit = 50,
        offset = 0,
        sortBy = "userName",
        sortOrder = "asc",
        search_by_user_name = "",
        search_by_email = "",
        search_by_agent_id = [],
        search_by_agent_category = [],
      } = params;

      // Query params for pagination and sorting
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sortBy,
        sortOrder,
      });

      // Request body for search/filter criteria - always include all fields
      const requestBody: any = {
        search_by_user_name: search_by_user_name?.trim() || "",
        search_by_email: search_by_email?.trim() || "",
        search_by_agent_id: search_by_agent_id || [],
        search_by_agent_category: search_by_agent_category || [],
      };

      const response = await axiosClient.post<UserAgentAccessResponse>(
        `/admin/user-agent-access?${queryParams.toString()}`,
        requestBody
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch user-agent mappings"
      );
    }
  }
);

// Update user's agents (add/remove)
export const updateUserAgents = createAsyncThunk(
  "userRoles/updateUserAgents",
  async (payload: UpdateUserAgentsPayload, { rejectWithValue }) => {
    try {
      const { userId, add = [], remove = [] } = payload;

      const response = await axiosClient.post<UpdateUserAgentsResponse>(
        `/admin/users/${userId}/agents`,
        { add, remove }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to update user agents"
      );
    }
  }
);

// Delete users from database
export const deleteUsers = createAsyncThunk(
  "userRoles/deleteUsers",
  async (payload: DeleteUsersPayload, { rejectWithValue }) => {
    try {
      const { userIds } = payload;

      const response = await axiosClient.delete<DeleteUsersResponse>(
        "/admin/users",
        { data: { userIds } }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to delete users"
      );
    }
  }
);

// Remove specific agent from user (wrapper using updateUserAgents)
export const removeAgentFromUser = createAsyncThunk(
  "userRoles/removeAgentFromUser",
  async (
    { userId, agentId }: { userId: string; agentId: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      // Use the updateUserAgents thunk to remove the agent
      const result = await dispatch(
        updateUserAgents({ userId, remove: [agentId] })
      ).unwrap();

      return { agentId, ...result };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to remove agent from user"
      );
    }
  }
);

// Add users to database (can add multiple)
export const addUsers = createAsyncThunk(
  "userRoles/addUsers",
  async (payload: AddUsersPayload, { rejectWithValue }) => {
    try {
      const { users } = payload;

      const response = await axiosClient.post<AddUsersResponse>(
        "/admin/users",
        { users }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to add users"
      );
    }
  }
);

// Bulk upload users to agent
export const bulkUploadUsers = createAsyncThunk(
  "userRoles/bulkUploadUsers",
  async (payload: BulkUploadPayload, { rejectWithValue }) => {
    try {
      const { agentId, addEmails, removeEmails, file } = payload;

      if (file) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append("file", file);

        const response = await axiosClient.post<BulkUploadResponse>(
          `/admin/users/bulk-upload?agent_id=${agentId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } else {
        // Use JSON payload for email lists
        const response = await axiosClient.post<BulkUploadResponse>(
          `/admin/users/bulk-upload?agent_id=${agentId}`,
          {
            add_email: addEmails || [],
            remove_email: removeEmails || [],
          }
        );
        return response.data;
      }
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to bulk upload users"
      );
    }
  }
);

// Assign multiple agents to multiple users (new optimized endpoint)
export const assignMultipleAgents = createAsyncThunk(
  "userRoles/assignMultipleAgents",
  async (payload: AssignMultipleAgentsPayload, { rejectWithValue }) => {
    try {
      const { userIds, agentIds, departments, addEmails, file } = payload;

      if (file) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append("file", file);

        if (agentIds && agentIds.length > 0) {
          formData.append("agent_ids", JSON.stringify(agentIds));
        } else if (departments && departments.length > 0) {
          formData.append("departments", JSON.stringify(departments));
        }

        const response = await axiosClient.post<AssignMultipleAgentsResponse>(
          "/admin/multiple-agents",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return response.data;
      } else {
        // Use JSON payload for email lists
        const requestBody: any = {};

        if (addEmails && addEmails.length > 0) {
          requestBody.add_email = addEmails;
        }

        if (userIds && userIds.length > 0) {
          requestBody.user_ids = userIds;
        }

        if (agentIds && agentIds.length > 0) {
          requestBody.agent_ids = agentIds;
        } else if (departments && departments.length > 0) {
          requestBody.departments = departments;
        }

        const response = await axiosClient.post<AssignMultipleAgentsResponse>(
          "/admin/multiple-agents",
          requestBody
        );
        return response.data;
      }
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to assign multiple agents"
      );
    }
  }
);

interface UserRolesState {
  users: User[];
  usersPagination: Pagination | null;
  usersLoading: boolean;
  agents: Agent[];
  agentsLoading: boolean;
  userAgentMappings: UserWithAgents[];
  mappingsPagination: Pagination | null;
  mappingsLoading: boolean;
  error: string | null;
  updateLoading: boolean;
  updateSuccess: boolean;
  updateError: string | null;
  deleteLoading: boolean;
  deleteSuccess: boolean;
  deleteError: string | null;
  deletingUserIds: string[];
  deletingAgentId: string | null;
  addUserLoading: boolean;
  addUserSuccess: boolean;
  addUserError: string | null;
  newlyAddedUser: AddUsersResponse | null;
  bulkUploadLoading: boolean;
  bulkUploadSuccess: boolean;
  bulkUploadError: string | null;
  bulkUploadResult: BulkUploadResponse | null;
}

const initialState: UserRolesState = {
  users: [],
  usersPagination: null,
  usersLoading: false,
  agents: [],
  agentsLoading: false,
  userAgentMappings: [],
  mappingsPagination: null,
  mappingsLoading: false,
  error: null,
  updateLoading: false,
  updateSuccess: false,
  updateError: null,
  deleteLoading: false,
  deleteSuccess: false,
  deleteError: null,
  deletingUserIds: [],
  deletingAgentId: null,
  addUserLoading: false,
  addUserSuccess: false,
  addUserError: null,
  newlyAddedUser: null,
  bulkUploadLoading: false,
  bulkUploadSuccess: false,
  bulkUploadError: null,
  bulkUploadResult: null,
};

const userRolesSlice = createSlice({
  name: "userRoles",
  initialState,
  reducers: {
    resetUpdateState: (state) => {
      state.updateSuccess = false;
      state.updateError = null;
    },
    resetDeleteState: (state) => {
      state.deleteSuccess = false;
      state.deleteError = null;
      state.deletingUserIds = [];
      state.deletingAgentId = null;
    },
    resetAddUserState: (state) => {
      state.addUserSuccess = false;
      state.addUserError = null;
      state.newlyAddedUser = null;
    },
    resetBulkUploadState: (state) => {
      state.bulkUploadSuccess = false;
      state.bulkUploadError = null;
      state.bulkUploadResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.data;
        state.usersPagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.error = action.error.message || "Failed to fetch users";
      })
      // Fetch agents
      .addCase(fetchAgents.pending, (state) => {
        state.agentsLoading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.agentsLoading = false;
        state.agents = action.payload;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.agentsLoading = false;
        state.error = action.error.message || "Failed to fetch agents";
      })
      // Fetch user-agent mappings
      .addCase(fetchUserAgentMappings.pending, (state) => {
        state.mappingsLoading = true;
        state.error = null;
      })
      .addCase(fetchUserAgentMappings.fulfilled, (state, action) => {
        state.mappingsLoading = false;
        state.userAgentMappings = action.payload.data;
        state.mappingsPagination = action.payload.pagination;
      })
      .addCase(fetchUserAgentMappings.rejected, (state, action) => {
        state.mappingsLoading = false;
        state.error = action.error.message || "Failed to fetch user-agent mappings";
      })
      // Update user agents
      .addCase(updateUserAgents.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.updateError = null;
      })
      .addCase(updateUserAgents.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;

        // Optimistically update the local state
        const { userId, remove = [] } = action.meta.arg;

        if (remove.length > 0) {
          // Find the user mapping and remove agents
          const mapping = state.userAgentMappings.find(
            (m) => m.user.id === userId
          );
          if (mapping) {
            mapping.agents = mapping.agents.filter(
              (agent) => !remove.includes(agent.id)
            );
          }
        }
      })
      .addCase(updateUserAgents.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.updateError = action.error.message || "Failed to update user agents";
      })
      // Delete users
      .addCase(deleteUsers.pending, (state, action) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteError = null;
        state.deletingUserIds = action.meta.arg.userIds;
      })
      .addCase(deleteUsers.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.deletingUserIds = [];
        // Optimistically remove users from mappings
        const deletedIds = action.meta.arg.userIds;
        state.userAgentMappings = state.userAgentMappings.filter(
          (mapping) => !deletedIds.includes(mapping.user.id)
        );
      })
      .addCase(deleteUsers.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = false;
        state.deleteError = action.error.message || "Failed to delete users";
        state.deletingUserIds = [];
      })
      // Remove agent from user
      .addCase(removeAgentFromUser.pending, (state, action) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteError = null;
        state.deletingAgentId = action.meta.arg.agentId;
      })
      .addCase(removeAgentFromUser.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.deletingAgentId = null;
        // Optimistically remove the agent from user's agents
        const { userId, agentId } = action.meta.arg;
        const mapping = state.userAgentMappings.find(
          (m) => m.user.id === userId
        );
        if (mapping) {
          mapping.agents = mapping.agents.filter(
            (agent) => agent.id !== agentId
          );
        }
      })
      .addCase(removeAgentFromUser.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = false;
        state.deleteError = action.error.message || "Failed to remove agent from user";
        state.deletingAgentId = null;
      })
      // Add users
      .addCase(addUsers.pending, (state) => {
        state.addUserLoading = true;
        state.addUserSuccess = false;
        state.addUserError = null;
        state.newlyAddedUser = null;
      })
      .addCase(addUsers.fulfilled, (state, action) => {
        state.addUserLoading = false;
        state.addUserSuccess = action.payload.added > 0;
        state.newlyAddedUser = action.payload;

        // Add successfully added users to the users list
        action.payload.results.forEach((result) => {
          if (result.success && result.user) {
            state.users.push({
              id: result.user.id.toString(),
              name: result.user.name,
              email: result.user.email,
            });
          }
        });
      })
      .addCase(addUsers.rejected, (state, action) => {
        state.addUserLoading = false;
        state.addUserSuccess = false;
        state.addUserError = (action.payload as string) || action.error.message || "Failed to add users";
      })
      // Bulk upload users
      .addCase(bulkUploadUsers.pending, (state) => {
        state.bulkUploadLoading = true;
        state.bulkUploadSuccess = false;
        state.bulkUploadError = null;
        state.bulkUploadResult = null;
      })
      .addCase(bulkUploadUsers.fulfilled, (state, action) => {
        state.bulkUploadLoading = false;
        state.bulkUploadSuccess = true;
        state.bulkUploadResult = action.payload;
      })
      .addCase(bulkUploadUsers.rejected, (state, action) => {
        state.bulkUploadLoading = false;
        state.bulkUploadSuccess = false;
        state.bulkUploadError = (action.payload as string) || action.error.message || "Failed to bulk upload users";
        state.bulkUploadResult = null;
      })
      // Assign multiple agents (new optimized endpoint)
      .addCase(assignMultipleAgents.pending, (state) => {
        state.bulkUploadLoading = true;
        state.bulkUploadSuccess = false;
        state.bulkUploadError = null;
        state.bulkUploadResult = null;
      })
      .addCase(assignMultipleAgents.fulfilled, (state, action) => {
        state.bulkUploadLoading = false;
        state.bulkUploadSuccess = true;
        state.bulkUploadResult = action.payload as any;
      })
      .addCase(assignMultipleAgents.rejected, (state, action) => {
        state.bulkUploadLoading = false;
        state.bulkUploadSuccess = false;
        state.bulkUploadError = (action.payload as string) || action.error.message || "Failed to assign multiple agents";
        state.bulkUploadResult = null;
      });
  },
});

export const { resetUpdateState, resetDeleteState, resetAddUserState, resetBulkUploadState } = userRolesSlice.actions;
export default userRolesSlice.reducer;
